import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://kakobuyspreadsheet.website";
const FAVICON_URL = "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png";

export default function SizingGuidePage() {
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Kakobuy Spreadsheet Sizing Guide: Never Order the Wrong Size 2026",
    "description": "Master Kakobuy sizing with our detailed measurement guide from the trusted spreadsheet covering international size conversions, fit tips, and category-specific sizing advice.",
    "author": {
      "@type": "Organization",
      "name": "Kakobuy Spreadsheet Team"
    },
    "datePublished": "2026-06-25",
    "dateModified": "2026-06-25",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": SITE_URL + "/guide/sizing-guide"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Kakobuy Spreadsheet",
      "logo": {
        "@type": "ImageObject",
        "url": FAVICON_URL
      }
    },
    "image": "https://readdy.ai/api/search-image?query=A%20measuring%20tape%20draped%20elegantly%20beside%20a%20neatly%20folded%20garment%20on%20a%20light%20cream%20fabric%20surface%20with%20a%20notebook%20showing%20handwritten%20measurements%20and%20a%20pencil%20soft%20overhead%20lighting%20from%20a%20pendant%20lamp%20creating%20a%20tailor%20studio%20atmosphere%20editorial%20photography%20with%20warm%20inviting%20composition&width=1400&height=600&seq=article-sizing-v2-09&orientation=landscape"
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
      <Navbar />
      <main>
        <article className="pt-24 md:pt-28">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-foreground-900 transition-colors mb-6 cursor-pointer">
              <i className="ri-arrow-left-line text-sm"></i>Back to Kakobuy Spreadsheet Home
            </Link>
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold mb-4">Sizing Guide</span>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4 text-balance">
              <span className="text-primary-500">Kakobuy Spreadsheet</span> Sizing Guide: Never Order the Wrong Size
            </h1>
            <div className="flex items-center gap-4 text-xs text-foreground-500 mb-8 pb-6 border-b border-background-200/70">
              <span>Last Updated: June 25, 2026</span><span className="w-1 h-1 rounded-full bg-foreground-400"></span><span>5 min read</span>
            </div>
          </div>

          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <div className="relative rounded-2xl overflow-hidden aspect-[21/9] mb-8">
              <img src="https://readdy.ai/api/search-image?query=A%20measuring%20tape%20draped%20elegantly%20beside%20a%20neatly%20folded%20garment%20on%20a%20light%20cream%20fabric%20surface%20with%20a%20notebook%20showing%20handwritten%20measurements%20and%20a%20pencil%20soft%20overhead%20lighting%20from%20a%20pendant%20lamp%20creating%20a%20tailor%20studio%20atmosphere%20editorial%20photography%20with%20warm%20inviting%20composition&width=1400&height=600&seq=article-sizing-v2-09&orientation=landscape"
                alt="Kakobuy Spreadsheet Sizing Guide" className="w-full h-full object-cover rounded-2xl" loading="lazy" />
            </div>

            <section className="mb-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">Why Sizing Is the Biggest Kakobuy Challenge</h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                Incorrect sizing is the number one reason for <strong>Kakobuy Spreadsheet</strong> shopping disappointment. Asian sizing standards differ significantly from Western sizes, and a "Large" on Kakobuy may correspond to a "Small" or "Medium" in US or European sizing. This is why our spreadsheet dedicates an entire column to sizing notes for every clothing listing.
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                The <strong>Kakobuy Spreadsheet</strong> sizing guide includes actual garment measurements rather than just label sizes. This is the most reliable way to determine fit, as it removes the ambiguity of inconsistent size labeling across different sellers and manufacturers.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">Category-Specific Sizing Tips</h2>
              <div className="space-y-3">
                {[
                  { cat: "Shoes", tip: "Always measure your foot in centimeters and compare to the insole length in the spreadsheet. Kakobuy shoe sizes typically run 1-1.5 sizes smaller than US sizing. The spreadsheet includes exact insole measurements from verified QC photos." },
                  { cat: "Hoodies & Sweaters", tip: "Focus on chest width and sleeve length measurements. Asian hoodies tend to have shorter sleeves and wider bodies. Our spreadsheet includes 'fits like' comparisons to familiar retail brands so you know exactly what to expect." },
                  { cat: "T-Shirts", tip: "Check both the shoulder width and length. A size chart might show the correct chest measurement, but if the shirt is shorter than expected, it will not fit properly. The spreadsheet includes both dimensions." },
                  { cat: "Pants", tip: "Waist, hip, and inseam measurements are all essential. Asian pants sizing often has narrower hips relative to the waist measurement. Look for listings in the spreadsheet that include all three dimensions." },
                ].map((item, i) => (
                  <div key={i} className="p-4 rounded-xl bg-background-100 border border-background-200/70">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-ruler-line text-accent-500 text-sm"></i>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900">{item.cat}</h3>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.tip}</p>
                  </div>
                ))}
              </div>
            </section>

            <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Link to="/" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-lg hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer">
                <i className="ri-home-4-line text-sm"></i>Back to Homepage
              </Link>
              <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-foreground-200 text-foreground-700 text-sm font-medium rounded-lg hover:border-foreground-400 transition-all duration-300 whitespace-nowrap cursor-pointer">
                Browse Spreadsheet <i className="ri-arrow-right-up-line text-sm"></i>
              </a>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}